import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  apiFetch,
  apiJson,
} from "../api/client";

import {
  tenantPath,
} from "../utils/tenantContext";

import "./PcapAnalystViewer.css";
import RemoteCaptureControl from "./RemoteCaptureControl";
import PcapEvidencePanel from "./PcapEvidencePanel";
import TargetVisibilityPanel from "./TargetVisibilityPanel";
import TargetCaptureGate from "./TargetCaptureGate";
import ObservabilityValidationPanel from "./ObservabilityValidationPanel";
import AttackReconstructionPanel from "./AttackReconstructionPanel";


function formatTime(value) {
  if (!value) return "—";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleString();
}


function endpoint(ip, port) {
  if (!ip) return "—";

  return port
    ? `${ip}:${port}`
    : ip;
}


function PcapAnalystViewer() {
  const [captures, setCaptures] =
    useState([]);

  const [
    selectedCapture,
    setSelectedCapture,
  ] = useState("");

  const [summary, setSummary] =
    useState(null);

  const [packets, setPackets] =
    useState([]);

  const [flows, setFlows] =
    useState([]);

  const [
    selectedPacket,
    setSelectedPacket,
  ] = useState(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [exporting, setExporting] =
    useState(false);


  const loadCapture = useCallback(
    async (captureId) => {
      if (!captureId) return;

      const encoded =
        encodeURIComponent(
          captureId
        );

      const [
        summaryData,
        packetData,
        flowData,
      ] = await Promise.all([
        apiJson(
          tenantPath(
            `/network-protection/pcap/captures/${encoded}/summary`
          )
        ),

        apiJson(
          tenantPath(
            `/network-protection/pcap/captures/${encoded}/packets?limit=5000`
          )
        ),

        apiJson(
          tenantPath(
            `/network-protection/pcap/captures/${encoded}/flows`
          )
        ),
      ]);

      setSummary(summaryData);

      setPackets(
        Array.isArray(
          packetData?.packets
        )
          ? packetData.packets
          : []
      );

      setFlows(
        Array.isArray(
          flowData?.flows
        )
          ? flowData.flows
          : []
      );

      setSelectedPacket(null);
    },
    []
  );


  const load = useCallback(
    async () => {
      try {
        setLoading(true);
        setError("");

        const data =
          await apiJson(
            tenantPath(
              "/network-protection/pcap/captures"
            )
          );

        const rows =
          Array.isArray(
            data?.captures
          )
            ? data.captures
            : [];

        setCaptures(rows);

        if (rows.length > 0) {
          const captureId =
            selectedCapture ||
            rows[0].capture_id;

          setSelectedCapture(
            captureId
          );

          await loadCapture(
            captureId
          );
        }
      } catch (loadError) {
        console.error(
          "PCAP analyst viewer:",
          loadError
        );

        setError(
          loadError.message ||
          "Unable to load capture."
        );
      } finally {
        setLoading(false);
      }
    },
    [
      loadCapture,
      selectedCapture,
    ]
  );


  useEffect(() => {
    load();
  }, [load]);


  // WIRELESSPRESS_R19_EXECUTIVE_EVIDENCE_REPORT_R1

  const selectedCaptureRecord =
    useMemo(
      () =>
        captures.find(
          (capture) =>
            capture.capture_id ===
            selectedCapture
        ) || null,
      [
        captures,
        selectedCapture,
      ]
    );


  const reportGeneratedAt =
    useMemo(
      () =>
        new Date().toLocaleString(),
      [
        selectedCapture,
        summary,
      ]
    );


  const selectedFlowCounts =
    useMemo(() => {
      const result = {};

      packets.forEach(
        (packet) => {
          const key =
            packet.flow_key;

          if (!key) return;

          result[key] =
            (result[key] || 0) + 1;
        }
      );

      return result;
    }, [packets]);


  const changeCapture =
    async (event) => {
      const captureId =
        event.target.value;

      setSelectedCapture(
        captureId
      );

      try {
        setLoading(true);
        setError("");

        await loadCapture(
          captureId
        );
      } catch (loadError) {
        setError(
          loadError.message ||
          "Unable to load capture."
        );
      } finally {
        setLoading(false);
      }
    };


  const printExecutiveReport =
    () => {
      window.print();
    };


  const exportPcap =
    async () => {
      if (!selectedCapture) return;

      const confirmed =
        window.confirm(
          "Export this authorized PCAP for offline analyst review?"
        );

      if (!confirmed) return;

      try {
        setExporting(true);

        const response =
          await apiFetch(
            tenantPath(
              `/network-protection/pcap/captures/${encodeURIComponent(
                selectedCapture
              )}/export?confirm=true`
            )
          );

        if (!response.ok) {
          let message =
            "PCAP export failed.";

          try {
            const body =
              await response.json();

            message =
              body.detail ||
              message;
          } catch (_) {
            // keep fallback
          }

          throw new Error(
            message
          );
        }

        const blob =
          await response.blob();

        const url =
          window.URL.createObjectURL(
            blob
          );

        const anchor =
          document.createElement(
            "a"
          );

        anchor.href = url;

        anchor.download =
          `wirelesspress-${selectedCapture}.pcap`;

        document.body.appendChild(
          anchor
        );

        anchor.click();
        anchor.remove();

        window.URL.revokeObjectURL(
          url
        );
      } catch (exportError) {
        window.alert(
          exportError.message
        );
      } finally {
        setExporting(false);
      }
    };


  return (
    <section className="pcapViewer">
      <header className="pcapViewerHeader">
        <div>
          <span>
            R19 PACKET FORENSICS
          </span>

          <h2>
            Analyst Packet Viewer
          </h2>

          <p>
            Wireshark-style packet,
            protocol, and conversation
            inspection for authorized
            bounded captures.
          </p>
        </div>

        <div className="pcapViewerControls">
          <select
            value={selectedCapture}
            onChange={changeCapture}
          >
            {captures.map(
              (capture) => (
                <option
                  value={
                    capture.capture_id
                  }
                  key={
                    capture.capture_id
                  }
                >
                  {capture.capture_id}
                </option>
              )
            )}
          </select>

          <button
            type="button"
            className="pcapReportButton"
            onClick={printExecutiveReport}
            disabled={
              !selectedCapture ||
              !summary
            }
          >
            Executive Evidence Report
          </button>

          <button
            type="button"
            onClick={exportPcap}
            disabled={
              !selectedCapture ||
              exporting
            }
          >
            {exporting
              ? "Exporting..."
              : "Export PCAP"}
          </button>
        </div>
      </header>

      <RemoteCaptureControl />

      <TargetVisibilityPanel />

      <ObservabilityValidationPanel />

      <TargetCaptureGate />

      <PcapEvidencePanel captureId={selectedCapture} />

      <AttackReconstructionPanel captureId={selectedCapture} />

      {error ? (
        <div className="pcapError">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="pcapLoading">
          Loading packet analysis...
        </div>
      ) : null}

      {!loading && summary ? (
        <>
          <div className="pcapMetrics">
            <article>
              <span>Frames</span>
              <strong>
                {
                  summary.summary
                    ?.frame_count || 0
                }
              </strong>
            </article>

            <article>
              <span>Flows</span>
              <strong>
                {
                  summary.summary
                    ?.flow_count || 0
                }
              </strong>
            </article>

            <article>
              <span>HTTP</span>
              <strong>
                {
                  summary.summary
                    ?.http_message_count ||
                  0
                }
              </strong>
            </article>

            <article>
              <span>DNS</span>
              <strong>
                {
                  summary.summary
                    ?.dns_query_count || 0
                }
              </strong>
            </article>

            <article>
              <span>TLS SNI</span>
              <strong>
                {
                  summary.summary
                    ?.tls_sni_count || 0
                }
              </strong>
            </article>
          </div>

          <section className="pcapBlock">
            <div className="pcapBlockTitle">
              <div>
                <span>
                  CONVERSATIONS
                </span>

                <h3>
                  Reconstructed Flows
                </h3>
              </div>
            </div>

            <div className="pcapFlowGrid">
              {flows.map(
                (flow) => (
                  <article
                    className="pcapFlow"
                    key={flow.flow_key}
                  >
                    <strong>
                      {endpoint(
                        flow.endpoint_a_ip,
                        flow.endpoint_a_port
                      )}
                    </strong>

                    <span>
                      ↔
                    </span>

                    <strong>
                      {endpoint(
                        flow.endpoint_b_ip,
                        flow.endpoint_b_port
                      )}
                    </strong>

                    <div>
                      <span>
                        {
                          flow.transport_protocol
                        }
                      </span>

                      <span>
                        {
                          selectedFlowCounts[
                            flow.flow_key
                          ] ||
                          flow.packet_count
                        }{" "}
                        packets
                      </span>

                      <span>
                        {
                          flow.original_bytes
                        }{" "}
                        bytes
                      </span>
                    </div>
                  </article>
                )
              )}
            </div>
          </section>

          <section className="pcapBlock">
            <div className="pcapBlockTitle">
              <div>
                <span>
                  PACKET LIST
                </span>

                <h3>
                  Frames
                </h3>
              </div>

              <small>
                Click a packet for decoded
                details.
              </small>
            </div>

            <div className="pcapTableWrap">
              <table className="pcapTable">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>Time</th>
                    <th>Source</th>
                    <th>Destination</th>
                    <th>Protocol</th>
                    <th>Length</th>
                    <th>Info</th>
                  </tr>
                </thead>

                <tbody>
                  {packets.map(
                    (packet) => {
                      const protocol =
                        packet
                          .application_protocol ||
                        packet
                          .transport_protocol ||
                        packet
                          .network_protocol ||
                        "Unknown";

                      const info =
                        packet.http_method
                          ? `${packet.http_method} ${packet.http_target || ""}`
                          : packet.http_status_line
                          ? packet.http_status_line
                          : packet.dns_query_name
                          ? packet.dns_query_name
                          : packet.tls_server_name
                          ? `SNI ${packet.tls_server_name}`
                          : Array.isArray(
                              packet.tcp_flags
                            )
                          ? packet.tcp_flags.join(
                              ", "
                            )
                          : "";

                      return (
                        <tr
                          key={
                            packet.frame_number
                          }
                          className={
                            selectedPacket
                              ?.frame_number ===
                            packet.frame_number
                              ? "selected"
                              : ""
                          }
                          onClick={() =>
                            setSelectedPacket(
                              packet
                            )
                          }
                        >
                          <td>
                            {
                              packet.frame_number
                            }
                          </td>

                          <td>
                            {formatTime(
                              packet.timestamp
                            )}
                          </td>

                          <td>
                            <code>
                              {endpoint(
                                packet.source_ip,
                                packet.source_port
                              )}
                            </code>
                          </td>

                          <td>
                            <code>
                              {endpoint(
                                packet.destination_ip,
                                packet.destination_port
                              )}
                            </code>
                          </td>

                          <td>
                            {protocol}
                          </td>

                          <td>
                            {
                              packet.original_length
                            }
                          </td>

                          <td>
                            {info}
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="pcapPacketDetail">
            <div className="pcapBlockTitle">
              <div>
                <span>
                  PACKET DETAIL
                </span>

                <h3>
                  {selectedPacket
                    ? `Frame ${selectedPacket.frame_number}`
                    : "Select a packet"}
                </h3>
              </div>
            </div>

            {selectedPacket ? (
              <div className="pcapDetailGrid">
                {Object.entries(
                  selectedPacket
                ).map(
                  ([key, value]) => (
                    <div key={key}>
                      <span>
                        {key}
                      </span>

                      <code>
                        {Array.isArray(value)
                          ? value.join(", ")
                          : typeof value ===
                            "object" &&
                            value !== null
                          ? JSON.stringify(
                              value
                            )
                          : String(
                              value ??
                                "—"
                            )}
                      </code>
                    </div>
                  )
                )}
              </div>
            ) : (
              <p className="pcapEmpty">
                Select a frame above to
                inspect Ethernet, IP,
                transport, flags, HTTP,
                DNS, or TLS metadata.
              </p>
            )}
          </section>

          <section className="pcapExecutiveReport">
            <div className="pcapReportClassification">
              CONFIDENTIAL
            </div>

            <div className="pcapReportBrand">
              <div>
                <span>
                  WIRELESS PRESS
                </span>

                <h2>
                  Executive Network Evidence Report
                </h2>

                <p>
                  Packet Inspection and Network Forensics
                </p>
              </div>

              <div className="pcapReportBadge">
                EVIDENCE REPORT
              </div>
            </div>

            <div className="pcapReportMeta">
              <div>
                <span>
                  Report Generated
                </span>

                <strong>
                  {reportGeneratedAt}
                </strong>
              </div>

              <div>
                <span>
                  Capture ID
                </span>

                <strong>
                  {selectedCapture || "—"}
                </strong>
              </div>

              <div>
                <span>
                  Capture Interface
                </span>

                <strong>
                  {
                    selectedCaptureRecord
                      ?.interface || "—"
                  }
                </strong>
              </div>

              <div>
                <span>
                  Evidence Classification
                </span>

                <strong>
                  CONFIDENTIAL
                </strong>
              </div>
            </div>

            <section className="pcapReportSection">
              <h3>
                Executive Summary
              </h3>

              <p>
                Wireless Press Network Inspection analyzed
                the selected authorized packet capture and
                reconstructed observable packet, protocol,
                and network conversation evidence.
              </p>

              <p>
                The report represents the evidence available
                within this specific capture window only and
                should not be interpreted as a complete
                representation of all network activity
                outside the recorded capture period.
              </p>
            </section>

            <section className="pcapReportSection">
              <h3>
                Evidence Summary
              </h3>

              <div className="pcapReportEvidenceGrid">
                <div>
                  <span>
                    Frames Analyzed
                  </span>

                  <strong>
                    {
                      summary.summary
                        ?.frame_count || 0
                    }
                  </strong>
                </div>

                <div>
                  <span>
                    Decoded Frames
                  </span>

                  <strong>
                    {
                      summary.summary
                        ?.decoded_frames || 0
                    }
                  </strong>
                </div>

                <div>
                  <span>
                    Network Flows
                  </span>

                  <strong>
                    {
                      summary.summary
                        ?.flow_count || 0
                    }
                  </strong>
                </div>

                <div>
                  <span>
                    HTTP Messages
                  </span>

                  <strong>
                    {
                      summary.summary
                        ?.http_message_count ||
                      0
                    }
                  </strong>
                </div>

                <div>
                  <span>
                    DNS Queries
                  </span>

                  <strong>
                    {
                      summary.summary
                        ?.dns_query_count || 0
                    }
                  </strong>
                </div>

                <div>
                  <span>
                    TLS Client Hello
                  </span>

                  <strong>
                    {
                      summary.summary
                        ?.tls_client_hello_count ||
                      0
                    }
                  </strong>
                </div>

                <div>
                  <span>
                    TLS SNI Observations
                  </span>

                  <strong>
                    {
                      summary.summary
                        ?.tls_sni_count || 0
                    }
                  </strong>
                </div>

                <div>
                  <span>
                    Capture Size
                  </span>

                  <strong>
                    {
                      selectedCaptureRecord
                        ?.pcap_bytes || 0
                    } bytes
                  </strong>
                </div>
              </div>
            </section>

            <section className="pcapReportSection">
              <h3>
                Evidence Integrity
              </h3>

              <div className="pcapReportHash">
                <span>
                  PCAP SHA-256
                </span>

                <code>
                  {
                    selectedCaptureRecord
                      ?.pcap_sha256 || "—"
                  }
                </code>
              </div>

              <div className="pcapReportIntegrityRows">
                <div>
                  <span>
                    Capture Start
                  </span>

                  <strong>
                    {formatTime(
                      selectedCaptureRecord
                        ?.started_at
                    )}
                  </strong>
                </div>

                <div>
                  <span>
                    Capture End
                  </span>

                  <strong>
                    {formatTime(
                      selectedCaptureRecord
                        ?.ended_at
                    )}
                  </strong>
                </div>

                <div>
                  <span>
                    Snap Length
                  </span>

                  <strong>
                    {
                      selectedCaptureRecord
                        ?.snaplen || "—"
                    }
                  </strong>
                </div>

                <div>
                  <span>
                    Full Packet Capture
                  </span>

                  <strong>
                    {
                      selectedCaptureRecord
                        ?.full_packet
                        ? "YES"
                        : "NO"
                    }
                  </strong>
                </div>
              </div>
            </section>

            <section className="pcapReportSection">
              <h3>
                Network Conversations
              </h3>

              <table className="pcapReportTable">
                <thead>
                  <tr>
                    <th>
                      Endpoint A
                    </th>

                    <th>
                      Endpoint B
                    </th>

                    <th>
                      Protocol
                    </th>

                    <th>
                      Packets
                    </th>

                    <th>
                      Bytes
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {flows.map(
                    (flow) => (
                      <tr
                        key={
                          flow.flow_key
                        }
                      >
                        <td>
                          {endpoint(
                            flow.endpoint_a_ip,
                            flow.endpoint_a_port
                          )}
                        </td>

                        <td>
                          {endpoint(
                            flow.endpoint_b_ip,
                            flow.endpoint_b_port
                          )}
                        </td>

                        <td>
                          {
                            flow.transport_protocol
                          }
                        </td>

                        <td>
                          {
                            flow.packet_count
                          }
                        </td>

                        <td>
                          {
                            flow.original_bytes
                          }
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </section>

            <section className="pcapReportSection">
              <h3>
                Protocol Evidence
              </h3>

              <div className="pcapReportProtocolList">
                <div>
                  <span>
                    Transport Protocols
                  </span>

                  <strong>
                    {Object.entries(
                      summary.protocols
                        ?.transport_protocols ||
                        {}
                    )
                      .map(
                        ([name, count]) =>
                          `${name}: ${count}`
                      )
                      .join(" · ") ||
                      "No transport protocol data"
                    }
                  </strong>
                </div>

                <div>
                  <span>
                    Application Protocols
                  </span>

                  <strong>
                    {Object.entries(
                      summary.protocols
                        ?.application_protocols ||
                        {}
                    )
                      .map(
                        ([name, count]) =>
                          `${name}: ${count}`
                      )
                      .join(" · ") ||
                      "No application protocol data"
                    }
                  </strong>
                </div>
              </div>
            </section>

            <section className="pcapReportSection">
              <h3>
                Analyst Evidence Statement
              </h3>

              <p>
                Packet records were decoded from the
                selected bounded PCAP evidence file.
                Network conversations were reconstructed
                from source and destination addressing,
                transport ports, timestamps, packet counts,
                and observable protocol metadata.
              </p>

              <p>
                HTTP metadata is reported only when visible
                in the captured packet data. DNS and TLS
                metadata are reported only when observable
                within the capture. TLS-encrypted payload
                content was not decrypted.
              </p>
            </section>

            <section className="pcapReportSection">
              <h3>
                Security and Collection Boundaries
              </h3>

              <div className="pcapReportBoundaryGrid">
                <span>
                  Automatic Blocking:
                  <strong> NO</strong>
                </span>

                <span>
                  Packet Injection:
                  <strong> NO</strong>
                </span>

                <span>
                  TLS Decryption:
                  <strong> NO</strong>
                </span>

                <span>
                  Automatic Capture:
                  <strong> NO</strong>
                </span>

                <span>
                  Capture Default:
                  <strong> OFF</strong>
                </span>

                <span>
                  Analyst PCAP Export:
                  <strong>
                    EXPLICIT CONFIRMATION
                  </strong>
                </span>
              </div>
            </section>

            <footer className="pcapReportFooter">
              <strong>
                CONFIDENTIAL
              </strong>

              <span>
                Wireless Press Network Security Evidence
              </span>

              <span>
                {selectedCapture}
              </span>
            </footer>
          </section>

          <div className="pcapBoundary">
            <strong>
              Analysis boundary
            </strong>

            <span>
              PCAP export requires explicit
              confirmation · TLS decryption
              off · no packet injection ·
              no automatic blocking
            </span>
          </div>
        </>
      ) : null}
    </section>
  );
}

export default PcapAnalystViewer;
